import { inject, injectable } from "inversify";
import ApiRepo from "./api.repo";
import { ApiPage } from "@/core/models/page.model";
import { Province } from "../models/address/province.model";
import { District } from "../models/address/district.model";
import { Ward } from "../models/address/ward.model";
import { GlobalUtils } from "@/app/home/utils/global.utils";
import { AppUserAreaType } from "../enums/global.enums";

const ALLOWED_AREAS = GlobalUtils.parseArray(process.env.REACT_APP_ALLOWED_AREA);

const ALLOWED_PROVINCES = ALLOWED_AREAS
    .filter(a => a.split("_")[0] === AppUserAreaType.Province)
    .map(a => a.split("_")[1])
    .filter(a => a.length > 0);

const ALLOWED_DISTRICTS = ALLOWED_AREAS
    .filter(a => a.split("_")[0] === AppUserAreaType.District)
    .map(a => a.split("_")[1])
    .filter(a => a.length > 0);

const ALLOWED_WARDS = ALLOWED_AREAS
    .filter(a => a.split("_")[0] === AppUserAreaType.Ward)
    .map(a => a.split("_")[1])
    .filter(a => a.length > 0);

@injectable()
export class AddressRepo {
    @inject(ApiRepo)
    private apiRepo!: ApiRepo;
    provinces: Map<string, Province> = new Map();
    districts: Map<string, District> = new Map();
    wards: Map<string, Ward> = new Map();

    private async getAllProvincesApi() {
        const response = await this.apiRepo.get<ApiPage<Province>>("/api/Province/all");
        if (response?.code === "00") {
            response.result.data.forEach(_record => {
                this.provinces.set(_record.id?.toString(), _record);
            })
        }
    }

    private async getAllDistrictsApi() {
        const response = await this.apiRepo.get<ApiPage<District>>("/api/District/all");
        if (response?.code === "00") {
            response.result.data.forEach(_record => {
                this.districts.set(_record.id?.toString(), _record);
            })
        }
    }

    private async getAllWardsApi() {
        const response = await this.apiRepo.get<ApiPage<Ward>>("/api/Ward/all");
        if (response?.code === "00") {
            response.result.data.forEach(_record => {
                this.wards.set(_record.id?.toString(), _record);
            })
        }
    }

    async getProvince(id: string) {
        if (this.provinces.size === 0) {
            console.log("Cache missed!");
            await this.getAllProvincesApi();
        }
        const record = this.provinces.get(id?.toString());
        return record;
    }

    async getDistrict(id: string) {
        if (this.districts.size === 0) {
            console.log("Cache missed!");
            await this.getAllDistrictsApi();
        }
        const record = this.districts.get(id?.toString());
        return record;
    }

    async getWard(id: string) {
        if (this.wards.size === 0) {
            console.log("Cache missed!");
            await this.getAllWardsApi();
        }
        const record = this.wards.get(id?.toString());
        return record;
    }

    async getAllProvinces() {
        if (this.provinces.size === 0) {
            console.log("Cache missed!");
            await this.getAllProvincesApi();
        }
        const records = Array.from(this.provinces).filter(([key]) => {
            if (ALLOWED_PROVINCES.length == 0) return true;
            return ALLOWED_PROVINCES.includes(key);
        });
        return records;
    }

    async getAllDistricts(provinceId?: string) {
        if (this.districts.size === 0) {
            console.log("Cache missed!");
            await this.getAllDistrictsApi();
        }
        const records = Array.from(this.districts);
        if (!provinceId) {
            return records;
        }
        return records.filter(e => e[1].provinceId === provinceId);
    }

    async getAllWards(districtId?: string) {
        if (this.wards.size === 0) {
            console.log("Cache missed!");
            await this.getAllWardsApi();
        }
        const records = Array.from(this.wards);
        if (!districtId) {
            return records;
        }
        return records.filter(e => e[1].districtId === districtId);;
    }

    async getFactoryWards() {
        const response = await this.apiRepo.get<ApiPage<Ward>>("/api/Ward/get-by-water-factory");
        if (response?.code === "00") {
            return response.result.data;
        }
        return [];
    }
}